import { useState } from 'react';
import { momReviews, type MomReview } from '../../../mocks/reviews';

interface MomReviewsProps {
  campId: number;
}

function StarRating({ rating, interactive = false, onRate }: { rating: number; interactive?: boolean; onRate?: (r: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = interactive ? (hovered || rating) >= star : rating >= star;
        return (
          <button
            key={star}
            type={interactive ? 'button' : 'button'}
            disabled={!interactive}
            onClick={() => interactive && onRate && onRate(star)}
            onMouseEnter={() => interactive && setHovered(star)}
            onMouseLeave={() => interactive && setHovered(0)}
            className={`w-5 h-5 flex items-center justify-center transition-transform ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
          >
            <i className={`${filled ? 'ri-star-fill text-[#F5C518]' : 'ri-star-line text-gray-300'} text-base`}></i>
          </button>
        );
      })}
    </div>
  );
}

interface ReviewCardProps {
  review: MomReview;
}

function ReviewCard({ review }: ReviewCardProps) {
  return (
    <div className="bg-gradient-to-br from-pink-50 to-orange-50 rounded-2xl p-4 border border-pink-100">
      <div className="flex items-start justify-between mb-2">
        <StarRating rating={review.rating} />
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E91E8C] to-[#F5C518] flex items-center justify-center flex-shrink-0 ml-2">
          <span className="text-white text-xs font-bold">{review.parentName.charAt(0)}</span>
        </div>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed italic mb-2">
        &ldquo;{review.reviewText}&rdquo;
      </p>
      <p className="text-xs font-semibold text-[#E91E8C]">
        – {review.parentName}, mom of a {review.childAge} year old
      </p>
    </div>
  );
}

interface ReviewFormProps {
  campId: number;
  onSubmitted: (review: MomReview) => void;
}

function ReviewForm({ campId, onSubmitted }: ReviewFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [parentName, setParentName] = useState('');
  const [childAge, setChildAge] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const MAX_CHARS = 500;

  const handleReviewChange = (val: string) => {
    if (val.length <= MAX_CHARS) {
      setReviewText(val);
      setCharCount(val.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!rating) return;
    if (reviewText.length > MAX_CHARS) return;

    setStatus('submitting');

    const formData = new URLSearchParams();
    formData.append('campId', String(campId));
    formData.append('parentName', parentName);
    formData.append('childAge', childAge);
    formData.append('rating', String(rating));
    formData.append('review', reviewText);

    try {
      await fetch('https://readdy.ai/api/form/d6ne4q2qoe30lj0v8mqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      const newReview: MomReview = {
        id: Date.now(),
        campId,
        parentName,
        childAge: parseInt(childAge, 10),
        rating,
        reviewText,
      };

      setStatus('success');
      onSubmitted(newReview);
      setParentName('');
      setChildAge('');
      setReviewText('');
      setRating(0);
      setCharCount(0);

      setTimeout(() => {
        setStatus('idle');
        setIsOpen(false);
      }, 2500);
    } catch {
      setStatus('error');
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-pink-200 rounded-2xl text-sm font-semibold text-[#E91E8C] hover:bg-pink-50 hover:border-pink-300 transition-all cursor-pointer whitespace-nowrap"
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-edit-line text-base"></i>
        </div>
        Write a Mom Review
      </button>
    );
  }

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl border border-pink-200 p-4 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-heart-line text-[#E91E8C]"></i>
          </div>
          Share Your Experience
        </h4>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer text-gray-400"
        >
          <i className="ri-close-line text-base"></i>
        </button>
      </div>

      {/* Star Rating */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Rating <span className="text-[#E91E8C]">*</span></label>
        <StarRating rating={rating} interactive onRate={setRating} />
        {rating === 0 && status === 'submitting' && (
          <p className="text-xs text-red-500 mt-1">Please select a rating</p>
        )}
      </div>

      {/* Parent Name */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Your First Name <span className="text-[#E91E8C]">*</span>
        </label>
        <input
          type="text"
          name="parentName"
          required
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          placeholder="e.g. Sarah"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 transition-all"
        />
      </div>

      {/* Child Age */}
      <div className="mb-3">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Child's Age <span className="text-[#E91E8C]">*</span>
        </label>
        <input
          type="number"
          name="childAge"
          required
          min={3}
          max={17}
          value={childAge}
          onChange={(e) => setChildAge(e.target.value)}
          placeholder="e.g. 9"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 transition-all"
        />
      </div>

      {/* Review Text */}
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-600 mb-1.5">
          Your Review <span className="text-[#E91E8C]">*</span>
          <span className="text-gray-400 font-normal ml-1">(1–3 sentences)</span>
        </label>
        <textarea
          name="review"
          required
          rows={3}
          value={reviewText}
          onChange={(e) => handleReviewChange(e.target.value)}
          placeholder="Tell other moms what you loved about this camp…"
          className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 transition-all resize-none"
        />
        <div className={`text-right text-xs mt-0.5 ${charCount >= MAX_CHARS ? 'text-red-500' : 'text-gray-400'}`}>
          {charCount}/{MAX_CHARS}
        </div>
      </div>

      {status === 'success' && (
        <div className="mb-3 flex items-center gap-2 bg-green-50 text-green-700 text-sm font-semibold px-3 py-2.5 rounded-xl border border-green-100">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-checkbox-circle-line text-base"></i>
          </div>
          Thank you! Your review has been submitted.
        </div>
      )}

      {status === 'error' && (
        <div className="mb-3 flex items-center gap-2 bg-red-50 text-red-600 text-sm font-semibold px-3 py-2.5 rounded-xl border border-red-100">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-error-warning-line text-base"></i>
          </div>
          Something went wrong. Please try again.
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={status === 'submitting' || charCount > MAX_CHARS}
          className="flex-1 py-2.5 bg-gradient-to-r from-[#E91E8C] to-[#D946A6] text-white text-sm font-bold rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {status === 'submitting' ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting…
            </span>
          ) : 'Submit Review'}
        </button>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="px-4 py-2.5 border-2 border-[#3D1A6E] text-[#3D1A6E] text-sm font-semibold rounded-xl hover:bg-purple-50 transition-colors whitespace-nowrap"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function MomReviews({ campId }: MomReviewsProps) {
  const [localReviews, setLocalReviews] = useState<MomReview[]>(
    momReviews.filter((r) => r.campId === campId)
  );

  const handleNewReview = (review: MomReview) => {
    setLocalReviews((prev) => [review, ...prev]);
  };

  const avgRating = localReviews.length
    ? Math.round((localReviews.reduce((sum, r) => sum + r.rating, 0) / localReviews.length) * 10) / 10
    : 0;

  return (
    <div>
      {/* Section Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-heart-fill text-[#E91E8C]"></i>
          </div>
          Mom Reviews
          {localReviews.length > 0 && (
            <span className="text-xs font-semibold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              {localReviews.length}
            </span>
          )}
        </h3>
        {localReviews.length > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-star-fill text-[#F5C518] text-sm"></i>
            </div>
            <span className="text-sm font-bold text-gray-800">{avgRating}</span>
            <span className="text-xs text-gray-400">/ 5</span>
          </div>
        )}
      </div>

      {/* Reviews List */}
      {localReviews.length > 0 ? (
        <div className="space-y-3 mb-4">
          {localReviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>
      ) : (
        <div className="text-center py-5 mb-4 bg-pink-50/50 rounded-2xl border border-pink-100">
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-pink-100 rounded-full">
            <i className="ri-chat-heart-line text-[#E91E8C] text-xl"></i>
          </div>
          <p className="text-sm text-gray-500 font-medium">No reviews yet</p>
          <p className="text-xs text-gray-400 mt-0.5">Be the first mom to share your experience!</p>
        </div>
      )}

      {/* Submit Form */}
      <ReviewForm campId={campId} onSubmitted={handleNewReview} />
    </div>
  );
}
